import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { authedFetch } from "../authFetch.js";
import { authedJson } from "../apiClient.js";
import { useToast } from "../toast/ToastProvider.jsx";
import { BizAvatar } from "./SearchableCombobox.jsx";

// ─── Styles (match ConfigPage design system) ─────────────────────────────────
const S = {
  card:  { background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 16, marginBottom: 14, overflow: "hidden" },
  cardBody: { padding: "4px 28px 22px" },
  cardHeader: (open) => ({
    display: "flex", alignItems: "center", gap: 12, padding: "18px 24px",
    cursor: "pointer", userSelect: "none", background: "#fff",
    borderBottom: open ? "1px solid #F1F5F9" : "none",
  }),
  h2:    { fontSize: 16, fontWeight: 700, color: "#0F172A", margin: 0, flex: 1 },
  subtitle: { fontSize: 12, color: "#94A3B8", margin: "2px 0 0", fontWeight: 500 },
  hint:  { fontSize: 13, color: "#64748B", margin: "14px 0 18px", lineHeight: 1.5 },
  label: { fontSize: 12, fontWeight: 600, color: "#64748B", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6, display: "block" },
  input: { width: "100%", padding: "10px 13px", borderRadius: 8, border: "1.5px solid #E2E8F0", fontSize: 14, color: "#0F172A", outline: "none", background: "#fff", boxSizing: "border-box" },
  select:{ width: "100%", padding: "10px 13px", borderRadius: 8, border: "1.5px solid #E2E8F0", fontSize: 14, color: "#0F172A", outline: "none", background: "#fff", boxSizing: "border-box" },
  row:   { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F1F5F9" },
  primary:{ padding: "11px 24px", borderRadius: 10, fontWeight: 600, fontSize: 14, border: "none", background: "#004e42", color: "#fff", cursor: "pointer" },
  secondary:{ padding: "10px 20px", borderRadius: 10, fontWeight: 600, fontSize: 14, border: "1.5px solid #E2E8F0", background: "#fff", color: "#374151", cursor: "pointer" },
  danger:{ padding: "8px 14px", borderRadius: 8, border: "1px solid #FECACA", background: "#fff", color: "#DC2626", fontSize: 13, fontWeight: 500, cursor: "pointer" },
  pill:  (on) => ({ padding: "6px 12px", borderRadius: 999, border: `1.5px solid ${on ? "#004e42" : "#E2E8F0"}`, background: on ? "#004e42" : "#fff", color: on ? "#fff" : "#64748B", fontSize: 12, fontWeight: 600, cursor: "pointer" }),
  toggle:(on) => ({
    width: 38, height: 22, borderRadius: 999, position: "relative", cursor: "pointer", border: "none",
    background: on ? "#22C55E" : "#E2E8F0", transition: "background .15s", padding: 0,
  }),
  toggleKnob:(on) => ({ position: "absolute", top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left .15s" }),
};

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

/**
 * On load we prefill env-backed fields with their current env values so admins
 * see the effective config at a glance instead of a cryptic empty field.
 *
 * On save we collapse any value that still matches the env default back to "",
 * so the "empty ⇒ use env" fallback is preserved end-to-end. This means env-var
 * changes still propagate automatically unless the admin actively typed a
 * different override.
 */
const ENV_BACKED_FIELDS = [
  ["integrations", "dataverseUrl"],
  ["entities",     "bookingLinkEntity"],
  ["entities",     "bookingServiceMappingEntity"],
  ["security",     "usersGroupId"],
  ["security",     "adminsGroupId"],
];

function applyEnvDefaults(settings, env) {
  if (!settings) return settings;
  const e = env || {};
  const out = structuredClone(settings);
  for (const [section, key] of ENV_BACKED_FIELDS) {
    if (!out[section]) out[section] = {};
    if (!out[section][key] && e[key]) out[section][key] = e[key];
  }
  return out;
}

function collapseEnvDefaults(settings, env) {
  if (!settings) return settings;
  const e = env || {};
  const out = structuredClone(settings);
  for (const [section, key] of ENV_BACKED_FIELDS) {
    if (out[section]?.[key] && out[section][key] === e[key]) {
      out[section][key] = "";
    }
  }
  return out;
}

function isFromEnv(settingsValue, envValue) {
  return Boolean(envValue) && settingsValue === envValue;
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{ ...S.toggle(checked), opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
      aria-pressed={checked}
    >
      <span style={S.toggleKnob(checked)} />
    </button>
  );
}

function Pill({ active, onClick, children }) {
  return <button type="button" onClick={onClick} style={S.pill(active)}>{children}</button>;
}

// ─── Multi-select checkbox list for Bookings businesses ────────────────────
// Admins pick every business the link builder is allowed to offer. Rendered
// inline (not a dropdown) because the count is small enough that visible
// checkboxes are clearer than a combobox with hidden state.
function BusinessMultiSelect({ selected, onChange, businesses, businessPhotos }) {
  const [query, setQuery] = useState("");
  const selectedSet = useMemo(
    () => new Set((selected || []).map((e) => e.toLowerCase())),
    [selected]
  );
  const q = query.trim().toLowerCase();
  const filtered = q
    ? businesses.filter((b) =>
        (b.displayName || "").toLowerCase().includes(q) ||
        (b.id || "").toLowerCase().includes(q)
      )
    : businesses;

  const toggle = (email) => {
    const exists = selectedSet.has(email.toLowerCase());
    const next = exists
      ? (selected || []).filter((e) => e.toLowerCase() !== email.toLowerCase())
      : [...(selected || []), email];
    onChange(next);
  };

  const selectAll = () => {
    onChange(businesses.map((b) => b.id).filter(Boolean));
  };
  const clearAll = () => onChange([]);

  if (businesses.length === 0) {
    return (
      <div style={{ padding: "12px 14px", border: "1.5px dashed #E2E8F0", borderRadius: 8, fontSize: 13, color: "#94A3B8" }}>
        No Bookings businesses available. Verify the app registration has Graph <code>Bookings.Read.All</code> consented.
      </div>
    );
  }

  return (
    <div style={{ border: "1.5px solid #E2E8F0", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
      <div style={{ display: "flex", gap: 8, padding: 8, borderBottom: "1px solid #F1F5F9", alignItems: "center" }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type to filter…"
          style={{ ...S.input, padding: "8px 11px", fontSize: 13, flex: 1 }}
        />
        <button type="button" onClick={selectAll} style={{ ...S.secondary, padding: "7px 12px", fontSize: 12 }}>
          Select all
        </button>
        <button type="button" onClick={clearAll} style={{ ...S.secondary, padding: "7px 12px", fontSize: 12 }}>
          Clear
        </button>
      </div>
      <div style={{ maxHeight: 260, overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "12px 14px", fontSize: 13, color: "#94A3B8" }}>No matches</div>
        ) : (
          filtered.map((biz) => {
            const checked = selectedSet.has((biz.id || "").toLowerCase());
            return (
              <label
                key={biz.id}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "9px 12px", fontSize: 13, cursor: "pointer",
                  borderBottom: "1px solid #F8FAFC",
                  background: checked ? "#F5FAFA" : "#fff",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(biz.id)}
                  style={{ width: 16, height: 16, accentColor: "#004e42", flexShrink: 0 }}
                />
                <BizAvatar
                  displayName={biz.displayName}
                  email={biz.id}
                  photoUrl={businessPhotos[biz.id] || businessPhotos[biz.id?.toLowerCase()]}
                />
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                  <span style={{ fontWeight: checked ? 600 : 500, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {biz.displayName}
                  </span>
                  {biz.displayName !== biz.id && (
                    <span style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {biz.id}
                    </span>
                  )}
                </div>
              </label>
            );
          })
        )}
      </div>
      <div style={{ padding: "8px 12px", borderTop: "1px solid #F1F5F9", fontSize: 12, color: "#64748B", background: "#F8FAFC" }}>
        {selected?.length || 0} selected
      </div>
    </div>
  );
}

// ─── Collapsible card wrapper ────────────────────────────────────────────────
// Every IANA zone the browser knows; the short fallback covers browsers
// without Intl.supportedValuesOf.
const ALL_TIME_ZONES = (() => {
  try { return Intl.supportedValuesOf("timeZone"); }
  catch { return ["America/Los_Angeles", "America/Denver", "America/Phoenix", "America/Chicago", "America/New_York", "Europe/London", "UTC"]; }
})();

function TimeZoneSelect({ value, onChange, emptyLabel }) {
  const options = value && !ALL_TIME_ZONES.includes(value) ? [value, ...ALL_TIME_ZONES] : ALL_TIME_ZONES;
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} style={S.select}>
      <option value="">{emptyLabel}</option>
      {options.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
    </select>
  );
}

function CollapsibleCard({ title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={S.card}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(v => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(v => !v); } }}
        style={S.cardHeader(open)}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s", color: "#64748B" }}>
          <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div style={{ flex: 1 }}>
          <h2 style={S.h2}>{title}</h2>
          {subtitle && <p style={S.subtitle}>{subtitle}</p>}
        </div>
      </div>
      {open && <div style={S.cardBody}>{children}</div>}
    </div>
  );
}

// Per-service control of Microsoft Bookings' own Reschedule / Cancel email
// buttons. Bookings follows each SERVICE's isCustomerAllowedToManageBooking
// (the per-appointment flag is ignored), so this is what actually hides them.
// Changes apply to Bookings immediately (not on "Save").
function MicrosoftButtonsPanel({ instance, account, toast }) {
  const [rows, setRows] = useState(null);
  const [linkActive, setLinkActive] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false); // strict double-click guard (state lags a render)

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await authedJson(instance, account, "/api/service-self-manage");
      setRows(data.value || []);
      setLinkActive(data.manageLinkActive !== false);
      setLoadErrors(data.errors || []);
    } catch (err) {
      setError(err.message || "Could not load services.");
    }
  }, [instance, account]);
  useEffect(() => { load(); }, [load]);

  const apply = async (targets, allow) => {
    if (!targets.length || inFlight.current) return;
    if (!allow && !window.confirm(
      `Hide Microsoft's Reschedule / Cancel buttons on ${targets.length === 1 ? `"${targets[0].displayName}"` : `${targets.length} services`}?

` +
      "This changes the service in Microsoft Bookings. It also affects bookings already made — including ones whose email has no \"Manage your booking\" link — and stops Microsoft's own booking page offering self-service changes."
    )) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const data = await authedJson(instance, account, "/api/service-self-manage", {
        method: "POST",
        body: JSON.stringify({ changes: targets.map((r) => ({ businessId: r.businessId, serviceId: r.serviceId, allow })) }),
      });
      const failed = (data.results || []).filter((r) => !r.ok);
      if (failed.length) {
        const names = failed.map((f) => targets.find((t) => t.serviceId === f.serviceId)?.displayName || f.serviceId);
        toast.error(`Couldn't update in Bookings: ${names.join(", ")}`);
      } else {
        toast.success(allow ? "Microsoft's buttons turned back on." : "Microsoft's buttons hidden.");
      }
      await load();
    } catch (err) {
      toast.error(err.message || "Could not update services.", { correlationId: err.correlationId });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const showing = (rows || []).filter((r) => r.microsoftButtons);
  const hideBlocked = !linkActive; // hiding now would leave customers no way to manage online
  return (
    <div style={{ marginTop: 16 }}>
      <label style={S.label}>Microsoft buttons by service</label>
      <p style={{ fontSize: 12, color: "#94A3B8", margin: "0 0 8px", lineHeight: 1.5 }}>
        Bookings shows its Reschedule / Cancel buttons based on each service's own setting. Hiding them here changes the service in Microsoft Bookings right away (it also stops Microsoft's own booking page offering self-service changes for that service).
      </p>
      {error && <p style={{ fontSize: 12, color: "#DC2626", margin: "0 0 8px" }}>{error}</p>}
      {rows && hideBlocked && (
        <p style={{ fontSize: 12, color: "#B45309", margin: "0 0 8px", lineHeight: 1.5 }}>
          Hiding is disabled: our "Manage your booking" link isn't being added to emails (it's switched off above, BOOKING_HOST_URL isn't set to an https address, or no signing secret). Hiding Microsoft's buttons now would leave customers no way to manage their booking online.
        </p>
      )}
      {loadErrors.map((e) => (
        <p key={e.businessId} style={{ fontSize: 12, color: "#DC2626", margin: "0 0 8px" }}>{e.businessId}: {e.error}</p>
      ))}
      {!rows && !error && <p style={{ fontSize: 13, color: "#94A3B8" }}>Loading services…</p>}
      {rows && (
        <>
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden", maxHeight: 320, overflowY: "auto" }}>
            {rows.length === 0 && <div style={{ padding: "14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>No services in the configured Bookings businesses.</div>}
            {rows.map((r, i) => (
              <div key={`${r.businessId}|${r.serviceId}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: i < rows.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "#0F172A", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.displayName}>{r.displayName}</div>
                  <div style={{ fontSize: 11, color: "#94A3B8" }}>{r.businessId}{r.hiddenFromCustomers ? " · hidden from Microsoft's booking page" : ""}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: r.microsoftButtons ? "#B45309" : "#15803D", whiteSpace: "nowrap" }}>
                  {r.microsoftButtons ? "Buttons shown" : "Buttons hidden"}
                </span>
                <button type="button" disabled={busy || (r.microsoftButtons && hideBlocked)} onClick={() => apply([r], !r.microsoftButtons)} style={{ ...S.secondary, padding: "6px 12px", fontSize: 12, opacity: busy || (r.microsoftButtons && hideBlocked) ? 0.5 : 1 }}>
                  {r.microsoftButtons ? "Hide" : "Show"}
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center" }}>
            <button
              type="button"
              disabled={busy || hideBlocked || showing.length === 0}
              onClick={() => apply(showing, false)}
              style={{ ...S.primary, padding: "8px 16px", fontSize: 13, opacity: busy || hideBlocked || showing.length === 0 ? 0.6 : 1 }}
            >
              {busy ? "Updating…" : `Hide on all (${showing.length})`}
            </button>
            <button type="button" disabled={busy} onClick={load} style={{ ...S.secondary, padding: "8px 14px", fontSize: 13 }}>Refresh</button>
          </div>
        </>
      )}
    </div>
  );
}

// Per-service overrides of an availability setting, saved with the rest of
// Settings (e.g. availability.calendarEventsByService { serviceId: mode }).
const CALENDAR_EVENT_LABELS = { busyOnly: "Respect \"Show as\"", allEvents: "Block all timed events" };
const ANYONE_AVAILABLE_LABELS = { show: "Show", hide: "Hide" };

function ServiceOverridePanel({ instance, account, settings, update, mapKey, defaultKey, defaultValue, labels, ariaLabel, rowNote }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    authedJson(instance, account, "/api/service-self-manage")
      .then((data) => setRows(data.value || []))
      .catch((err) => setError(err.message || "Could not load services."));
  }, [instance, account]);

  const byService = settings.availability?.[mapKey] || {};
  const fallback = labels[settings.availability?.[defaultKey] ?? defaultValue];
  const set = (serviceId, mode) => update((s) => {
    const next = { ...(s.availability?.[mapKey] || {}) };
    if (mode) next[serviceId] = mode; else delete next[serviceId];
    s.availability = { ...(s.availability || {}), [mapKey]: next };
  });

  return (
    <div style={{ marginTop: 16 }}>
      <label style={S.label}>By service</label>
      {error && <p style={{ fontSize: 12, color: "#DC2626", margin: "0 0 8px" }}>{error}</p>}
      {!rows && !error && <p style={{ fontSize: 13, color: "#94A3B8" }}>Loading services…</p>}
      {rows && (
        <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden", maxHeight: 320, overflowY: "auto" }}>
          {rows.length === 0 && <div style={{ padding: "14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>No services in the configured Bookings businesses.</div>}
          {rows.map((r, i) => (
            <div key={`${r.businessId}|${r.serviceId}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: i < rows.length - 1 ? "1px solid #F1F5F9" : "none" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "#0F172A", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.displayName}>{r.displayName}</div>
                <div style={{ fontSize: 11, color: "#94A3B8" }}>{r.businessId}{rowNote?.(r) ? <span style={{ color: "#B45309" }}> · {rowNote(r)}</span> : null}</div>
              </div>
              <select
                aria-label={`${ariaLabel} — ${r.displayName}`}
                value={byService[r.serviceId] || ""}
                onChange={(e) => set(r.serviceId, e.target.value)}
                style={{ ...S.input, width: "auto", padding: "6px 8px", fontSize: 12 }}
              >
                <option value="">Default ({fallback})</option>
                {Object.entries(labels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function SettingsTab({ instance, account, onSaved }) {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [initial, setInitial]   = useState(null);
  const [envDefaults, setEnvDefaults] = useState({});
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [securityGroups, setSecurityGroups] = useState([]);
  const [groupsError, setGroupsError] = useState(null);
  const [bookingBusinesses, setBookingBusinesses] = useState([]);
  const [businessPhotos, setBusinessPhotos] = useState({});
  const [businessesError, setBusinessesError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // Load settings + relationship types in parallel
  useEffect(() => {
    if (!account) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [settingsRes, rtRes, groupsRes, cqRes, businessesRes] = await Promise.all([
          authedFetch(instance, account, "/api/settings"),
          fetch("/api/lead-relationship-types"),
          authedFetch(instance, account, "/api/security-groups"),
          fetch("/api/custom-questions"),
          authedFetch(instance, account, "/api/booking-businesses?withTimeZones=1"),
        ]);

        if (!settingsRes.ok) {
          const body = await settingsRes.json().catch(() => ({}));
          const e = new Error(body.error || "Could not load settings.");
          e.correlationId = body.correlationId;
          throw e;
        }
        const settingsData = await settingsRes.json();
        const rtData = rtRes.ok ? await rtRes.json() : { value: [] };

        if (cancelled) return;
        const env = settingsData.envDefaults || {};
        const prefilled = applyEnvDefaults(settingsData.settings, env);
        setSettings(prefilled);
        setInitial(JSON.stringify(prefilled));
        setEnvDefaults(env);
        setRelationshipTypes(rtData.value || []);

        if (cqRes.ok) {
          const cqData = await cqRes.json();
          const list = cqData.value || cqData || [];
          // Merge: the per-business loads below may already have added questions.
          setCustomQuestions((prev) => {
            const byId = new Map(prev.map((q) => [q.id, q]));
            for (const q of list) byId.set(q.id, q);
            return [...byId.values()];
          });
        } else {
          console.warn("[SettingsTab] Custom questions load failed:", cqRes.status);
        }

        if (groupsRes.ok) {
          const groupsData = await groupsRes.json();
          setSecurityGroups(groupsData.value || []);
        } else {
          const body = await groupsRes.json().catch(() => ({}));
          setGroupsError(
            `Could not load security groups. ` +
            `Verify the app registration has Graph Group.Read.All (admin-consented).` +
            (body.correlationId ? ` (Reference: ${body.correlationId})` : "")
          );
          console.warn("[SettingsTab] Security groups load failed:", groupsRes.status, body);
        }

        if (businessesRes.ok) {
          const businessesData = await businessesRes.json();
          // Alphabetical by displayName (case-insensitive) — matches the order
          // used in the link builder's per-row business picker.
          const sorted = (businessesData.value || []).slice().sort((a, b) =>
            (a.displayName || a.id || "").localeCompare(b.displayName || b.id || "", undefined, { sensitivity: "base" })
          );
          setBookingBusinesses(sorted);
          // Fetch owner photos (personal Bookings mailboxes have a real user
          // behind them; shared mailboxes just fall back to initials).
          const emails = sorted.map((b) => b.id).filter(Boolean);
          if (emails.length) {
            fetch(`/api/staff-photo?emails=${encodeURIComponent(emails.join(","))}`)
              .then((r) => (r.ok ? r.json() : {}))
              .then((photos) => { if (!cancelled) setBusinessPhotos(photos || {}); })
              .catch((err) => console.warn("[SettingsTab] Business photos fetch failed:", err));
          }
        } else {
          const body = await businessesRes.json().catch(() => ({}));
          setBusinessesError(
            `Could not load Bookings businesses. ` +
            `Verify the app registration has Graph Bookings.Read.All (admin-consented).` +
            (body.correlationId ? ` (Reference: ${body.correlationId})` : "")
          );
          console.warn("[SettingsTab] Booking businesses load failed:", businessesRes.status, body);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          toast.error(err.message || "Could not load settings.", { correlationId: err.correlationId });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [instance, account]);

  const dirty = useMemo(() => {
    if (!settings || !initial) return false;
    return JSON.stringify(settings) !== initial;
  }, [settings, initial]);

  const update = useCallback((updater) => {
    setSettings((prev) => {
      const next = structuredClone(prev);
      updater(next);
      return next;
    });
  }, []);

  const handleSave = async () => {
    if (!settings || saving) return;

    // Required: at least one Bookings business must be selected. Blocked here
    // so the admin gets an immediate error instead of a 400 from the backend
    // validator.
    const emails = settings.integrations?.bookingsBusinessEmails || [];
    if (!Array.isArray(emails) || emails.length === 0) {
      setError("Select at least one Bookings business under Integrations.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Collapse prefilled-from-env values back to "" so the fallback chain
      // is preserved — env changes continue to propagate automatically.
      const forSave = collapseEnvDefaults(settings, envDefaults);
      // Drop half-filled "Duplicate Questions" rows (no question picked yet).
      if (Array.isArray(forSave.customQuestionAutofill?.map)) {
        forSave.customQuestionAutofill = {
          ...forSave.customQuestionAutofill,
          map: forSave.customQuestionAutofill.map.filter((m) => m?.questionId),
        };
      }
      const data = await authedJson(instance, account, "/api/settings", {
        method: "PUT",
        body: JSON.stringify({ settings: forSave }),
      });
      const prefilled = applyEnvDefaults(data.settings, envDefaults);
      setSettings(prefilled);
      setInitial(JSON.stringify(prefilled));
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
      toast.success("Settings saved.");
      // Notify the parent (ConfigPage) so sibling tabs/pickers see the change
      // immediately without a browser refresh.
      onSaved?.(data.settings);
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Could not save settings.", { correlationId: err.correlationId });
    } finally {
      setSaving(false);
    }
  };

  // Custom-question definitions are per Bookings business; the initial load
  // only covers the default one. Merge in every configured business so the
  // question pickers below offer all of them.
  const bizEmailsKey = (settings?.integrations?.bookingsBusinessEmails || []).join(",");
  useEffect(() => {
    if (!bizEmailsKey) return;
    let cancelled = false;
    Promise.all(bizEmailsKey.split(",").map((biz) =>
      fetch(`/api/custom-questions?businessId=${encodeURIComponent(biz)}`)
        .then((r) => (r.ok ? r.json() : { value: [] }))
        .then((d) => d.value || [])
        .catch(() => [])
    )).then((lists) => {
      if (cancelled) return;
      setCustomQuestions((prev) => {
        const byId = new Map(prev.map((q) => [q.id, q]));
        for (const q of lists.flat()) byId.set(q.id, q);
        return [...byId.values()];
      });
    });
    return () => { cancelled = true; };
  }, [bizEmailsKey]);

  if (loading) {
    return (
      <div style={{ ...S.card, textAlign: "center", padding: "48px 28px" }}>
        <div style={{ width: 28, height: 28, border: "3px solid #E2E8F0", borderTopColor: "#004e42", borderRadius: "50%", animation: "spin .75s linear infinite", margin: "0 auto 14px" }} />
        <p style={{ fontSize: 13, color: "#64748B", margin: 0 }}>Loading settings…</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div style={{ ...S.card, textAlign: "center", padding: "28px" }}>
        <p style={{ color: "#DC2626", fontSize: 14 }}>{error || "Could not load settings."}</p>
      </div>
    );
  }

  return (
    <>
      {/* ── Integrations ─────────────────────────────────────────────── */}
      <CollapsibleCard
        title="Integrations"
        subtitle="Dataverse URL and Bookings businesses"
        defaultOpen
      >
        <p style={S.hint}>
          Override the backend endpoints without redeploying. Leave a field empty to use the server default (env var).
        </p>

        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Dataverse environment URL</label>
          <input
            type="url"
            value={settings.integrations?.dataverseUrl ?? ""}
            onChange={(e) => update((s) => { s.integrations.dataverseUrl = e.target.value; })}
            placeholder={envDefaults.dataverseUrl || "https://yourorg.crm.dynamics.com"}
            style={S.input}
          />
          <p style={{ fontSize: 12, color: "#94A3B8", margin: "6px 0 0" }}>
            {isFromEnv(settings.integrations?.dataverseUrl, envDefaults.dataverseUrl)
              ? <>Prefilled from <code>DATAVERSE_URL</code> env var. Edit to override, or clear to keep following the env.</>
              : <>Full HTTPS URL. Clear the field to fall back to <code>DATAVERSE_URL</code> env var.</>}
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Environment label</label>
          <input
            type="text"
            value={settings.integrations?.environmentLabel ?? ""}
            onChange={(e) => update((s) => { s.integrations.environmentLabel = e.target.value; })}
            placeholder="production"
            maxLength={50}
            style={S.input}
          />
          <p style={{ fontSize: 12, color: "#94A3B8", margin: "6px 0 0" }}>
            Free-form name for this deployment (e.g. <code>production</code>, <code>staging</code>). Log-only diagnostic — included in every "Creating/Updating lead" log line alongside the effective Dataverse URL so cross-environment mishaps are easy to spot in Application Insights. Not written to the Lead record. Leave empty to disable.
          </p>
        </div>

        <div>
          <label style={S.label}>
            Bookings businesses <span style={{ color: "#DC2626" }}>*</span>
          </label>
          <BusinessMultiSelect
            selected={settings.integrations?.bookingsBusinessEmails ?? []}
            onChange={(next) => update((s) => { s.integrations.bookingsBusinessEmails = next; })}
            businesses={bookingBusinesses}
            businessPhotos={businessPhotos}
          />
          {businessesError && (
            <p style={{ fontSize: 12, color: "#DC2626", margin: "6px 0 0" }}>{businessesError}</p>
          )}
          <p style={{ fontSize: 12, color: "#94A3B8", margin: "6px 0 0" }}>
            Select every Bookings business the link builder is allowed to use. The Booking Links tab only offers services from the businesses checked here. At least one must be selected.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <label style={S.label}>Bookings business timezones</label>
          <p style={{ fontSize: 12, color: "#94A3B8", margin: "0 0 8px" }}>
            The timezone each business's hours are set in. Service availability hours such as "8:00 AM – 7:00 PM" are read in this zone and converted for each visitor. It's detected automatically from Microsoft Bookings (Business information → time zone); set a zone here only to override it.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "#0F172A", fontWeight: 600 }}>Default (all businesses)</span>
            <TimeZoneSelect
              value={settings.integrations?.bookingsDefaultTimeZone ?? ""}
              onChange={(tz) => update((s) => { s.integrations.bookingsDefaultTimeZone = tz; })}
              emptyLabel="Not set — only used when Bookings doesn't report a zone"
            />
            {(settings.integrations?.bookingsBusinessEmails ?? []).map((email) => {
              const biz = (bookingBusinesses || []).find((b) => (b.id || "").toLowerCase() === email.toLowerCase());
              return (
                <Fragment key={email}>
                  <span style={{ minWidth: 0 }} title={email}>
                    <span style={{ display: "block", fontSize: 13, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{biz?.displayName || email}</span>
                    <span style={{ display: "block", fontSize: 11, color: biz?.detectedTimeZone ? "#15803D" : "#B45309" }}>
                      {biz?.detectedTimeZone ? `Detected from Bookings: ${biz.detectedTimeZone.replace(/_/g, " ")}` : "Not detected — set an override or the default"}
                    </span>
                  </span>
                  <TimeZoneSelect
                    value={settings.integrations?.bookingsBusinessTimeZones?.[email] ?? ""}
                    onChange={(tz) => update((s) => {
                      const map = { ...(s.integrations.bookingsBusinessTimeZones || {}) };
                      if (tz) map[email] = tz; else delete map[email];
                      s.integrations.bookingsBusinessTimeZones = map;
                    })}
                    emptyLabel={biz?.detectedTimeZone ? "Use detected" : "Use default"}
                  />
                </Fragment>
              );
            })}
          </div>
        </div>
      </CollapsibleCard>

      {/* ── Entities ─────────────────────────────────────────────────── */}
      <CollapsibleCard
        title="Entities"
        subtitle="Dataverse table logical names"
      >
        <p style={S.hint}>
          Dataverse custom table names for booking links and their service mappings. Empty = server default.
        </p>

        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Booking Link entity</label>
          <input
            type="text"
            value={settings.entities?.bookingLinkEntity ?? ""}
            onChange={(e) => update((s) => { s.entities.bookingLinkEntity = e.target.value; })}
            placeholder={envDefaults.bookingLinkEntity || "mek_bookinglinks"}
            style={{ ...S.input, fontFamily: "monospace" }}
          />
          <p style={{ fontSize: 12, color: "#94A3B8", margin: "6px 0 0" }}>
            {isFromEnv(settings.entities?.bookingLinkEntity, envDefaults.bookingLinkEntity)
              ? <>Prefilled from <code>BOOKING_LINK_ENTITY</code> env var. Edit to override, or clear to keep following the env.</>
              : <>Clear the field to fall back to <code>BOOKING_LINK_ENTITY</code> env var.</>}
          </p>
        </div>

        <div>
          <label style={S.label}>Booking Service Mapping entity</label>
          <input
            type="text"
            value={settings.entities?.bookingServiceMappingEntity ?? ""}
            onChange={(e) => update((s) => { s.entities.bookingServiceMappingEntity = e.target.value; })}
            placeholder={envDefaults.bookingServiceMappingEntity || "mek_bookingservicemappings"}
            style={{ ...S.input, fontFamily: "monospace" }}
          />
          <p style={{ fontSize: 12, color: "#94A3B8", margin: "6px 0 0" }}>
            {isFromEnv(settings.entities?.bookingServiceMappingEntity, envDefaults.bookingServiceMappingEntity)
              ? <>Prefilled from <code>BOOKING_SERVICE_MAPPING_ENTITY</code> env var. Edit to override, or clear to keep following the env.</>
              : <>Clear the field to fall back to <code>BOOKING_SERVICE_MAPPING_ENTITY</code> env var.</>}
          </p>
        </div>
      </CollapsibleCard>

      {/* ── Security ─────────────────────────────────────────────────── */}
      <CollapsibleCard
        title="Security"
        subtitle="Users and Admins Entra security groups"
      >
        <p style={S.hint}>
          Select which Entra ID security group controls access to each role.
          <br />
          <strong>Users</strong> can manage booking links. <strong>Admins</strong> can also edit these settings.
          If the Admins group is unset, all Users are treated as Admins.
        </p>

        {groupsError && (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", marginBottom: 16 }}>
            <p style={{ fontSize: 12, color: "#DC2626", margin: 0 }}>{groupsError}</p>
          </div>
        )}

        {(() => {
          const envGroupLabel = (groupId) => {
            if (!groupId) return null;
            const g = securityGroups.find(x => x.id === groupId);
            return g ? g.displayName : `${groupId.slice(0, 8)}…`;
          };
          const usersEnvLabel  = envGroupLabel(envDefaults.usersGroupId);
          const adminsEnvLabel = envGroupLabel(envDefaults.adminsGroupId);
          return <>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Users group</label>
              <select
                value={settings.security?.usersGroupId ?? ""}
                onChange={(e) => update((s) => { s.security.usersGroupId = e.target.value; })}
                style={S.select}
                disabled={securityGroups.length === 0}
              >
                <option value="">
                  {usersEnvLabel
                    ? `— use env var: ${usersEnvLabel} —`
                    : "— use USERS_SECURITY_GROUP_ID env var —"}
                </option>
                {securityGroups.map((g) => (
                  <option key={g.id} value={g.id}>{g.displayName}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={S.label}>Admins group</label>
              <select
                value={settings.security?.adminsGroupId ?? ""}
                onChange={(e) => update((s) => { s.security.adminsGroupId = e.target.value; })}
                style={S.select}
                disabled={securityGroups.length === 0}
              >
                <option value="">
                  {adminsEnvLabel
                    ? `— use env var: ${adminsEnvLabel} —`
                    : "— use ADMINS_SECURITY_GROUP_ID env var (or Users as fallback) —"}
                </option>
                {securityGroups.map((g) => (
                  <option key={g.id} value={g.id}>{g.displayName}</option>
                ))}
              </select>
            </div>
          </>;
        })()}
      </CollapsibleCard>

      {/* ── Services ─────────────────────────────────────────────────── */}
      <CollapsibleCard
        title="Services"
        subtitle="Service selection behavior"
      >
        <p style={S.hint}>Controls how services are selected during the booking flow.</p>
        <div style={S.row}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>Allow multiple service selection</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>When enabled, attendees can pick more than one service per booking.</div>
          </div>
          <Toggle
            checked={settings.services.allowMultipleSelection}
            onChange={(v) => update((s) => { s.services.allowMultipleSelection = v; })}
          />
        </div>
      </CollapsibleCard>

      {/* ── Relationship Type step ───────────────────────────────────── */}
      <CollapsibleCard
        title="Relationship Type step"
        subtitle="Show, skip via URL param, or skip via service custom question"
      >
        <p style={S.hint}>When the relationship is already known, skip Step&nbsp;1 and map a URL parameter or a service custom-question answer to the internal Relationship Type.</p>

        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Mode</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Pill active={settings.steps.relationshipType.mode === "show"}        onClick={() => update((s) => { s.steps.relationshipType.mode = "show"; })}>Show step</Pill>
            <Pill active={settings.steps.relationshipType.mode === "skipUrl"}     onClick={() => update((s) => { s.steps.relationshipType.mode = "skipUrl"; })}>Skip: URL param</Pill>
            <Pill active={settings.steps.relationshipType.mode === "skipService"} onClick={() => update((s) => {
              s.steps.relationshipType.mode = "skipService";
              // Auto-seed the answer map with the current Lead Relationship Type
              // options so admins start with a working table they can edit.
              if ((s.steps.relationshipType.serviceAnswerMap || []).length === 0 && relationshipTypes.length > 0) {
                s.steps.relationshipType.serviceAnswerMap = relationshipTypes.map(rt => ({
                  answer: rt.label,
                  optionSetValue: rt.value,
                }));
              }
            })}>Skip: Use Service</Pill>
          </div>
        </div>

        {settings.steps.relationshipType.mode === "skipUrl" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>URL parameter name</label>
              <input
                type="text"
                value={settings.steps.relationshipType.urlParamName}
                onChange={(e) => update((s) => { s.steps.relationshipType.urlParamName = e.target.value; })}
                placeholder="relationship"
                style={S.input}
              />
              <p style={{ fontSize: 12, color: "#94A3B8", margin: "6px 0 0" }}>
                Example: <code>?{settings.steps.relationshipType.urlParamName || "relationship"}=cpa</code>
              </p>
            </div>

            <div>
              <label style={S.label}>URL value → Relationship Type</label>
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
                {settings.steps.relationshipType.urlParamMap.length === 0 && (
                  <div style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>No mappings yet — add one below.</div>
                )}
                {settings.steps.relationshipType.urlParamMap.map((entry, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, padding: "10px 12px", borderBottom: i < settings.steps.relationshipType.urlParamMap.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                    <input
                      type="text"
                      value={entry.urlValue}
                      placeholder="url value (e.g. cpa)"
                      onChange={(e) => update((s) => { s.steps.relationshipType.urlParamMap[i].urlValue = e.target.value; })}
                      style={S.input}
                    />
                    <select
                      value={entry.optionSetValue ?? ""}
                      onChange={(e) => update((s) => { s.steps.relationshipType.urlParamMap[i].optionSetValue = Number(e.target.value); })}
                      style={S.select}
                    >
                      <option value="" disabled>Select relationship type…</option>
                      {relationshipTypes.map((rt) => (
                        <option key={rt.value} value={rt.value}>{rt.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => update((s) => { s.steps.relationshipType.urlParamMap.splice(i, 1); })}
                      style={S.danger}
                    >Remove</button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => update((s) => { s.steps.relationshipType.urlParamMap.push({ urlValue: "", optionSetValue: relationshipTypes[0]?.value ?? 0 }); })}
                style={{ ...S.secondary, marginTop: 10 }}
              >+ Add mapping</button>
            </div>
          </>
        )}

        {settings.steps.relationshipType.mode === "skipService" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Relationship question</label>
              <select
                value={settings.steps.relationshipType.serviceQuestionId || ""}
                onChange={(e) => update((s) => { s.steps.relationshipType.serviceQuestionId = e.target.value; })}
                style={S.select}
                disabled={customQuestions.length === 0}
              >
                <option value="">— Auto-detect by name (&quot;are you a…&quot;, &quot;relationship&quot;) —</option>
                {customQuestions.map((q) => (
                  <option key={q.id} value={q.id}>{q.displayName}</option>
                ))}
              </select>
              <p style={{ fontSize: 12, color: "#94A3B8", margin: "6px 0 0" }}>
                The Bookings custom question whose answer decides the Relationship Type. Leave blank to auto-detect across the service&apos;s questions.
              </p>
            </div>

            <div>
              <label style={S.label}>Answer → Relationship Type</label>
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
                {settings.steps.relationshipType.serviceAnswerMap.length === 0 && (
                  <div style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>No mappings yet — add one below.</div>
                )}
                {settings.steps.relationshipType.serviceAnswerMap.map((entry, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, padding: "10px 12px", borderBottom: i < settings.steps.relationshipType.serviceAnswerMap.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                    <input
                      type="text"
                      value={entry.answer}
                      placeholder="answer text (e.g. Existing Client)"
                      onChange={(e) => update((s) => { s.steps.relationshipType.serviceAnswerMap[i].answer = e.target.value; })}
                      style={S.input}
                    />
                    <select
                      value={entry.optionSetValue ?? ""}
                      onChange={(e) => update((s) => { s.steps.relationshipType.serviceAnswerMap[i].optionSetValue = Number(e.target.value); })}
                      style={S.select}
                    >
                      <option value="" disabled>Select relationship type…</option>
                      {relationshipTypes.map((rt) => (
                        <option key={rt.value} value={rt.value}>{rt.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => update((s) => { s.steps.relationshipType.serviceAnswerMap.splice(i, 1); })}
                      style={S.danger}
                    >Remove</button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => update((s) => { s.steps.relationshipType.serviceAnswerMap.push({ answer: "", optionSetValue: relationshipTypes[0]?.value ?? 0 }); })}
                style={{ ...S.secondary, marginTop: 10 }}
              >+ Add mapping</button>
            </div>
          </>
        )}
      </CollapsibleCard>

      {/* ── UTM parameters ───────────────────────────────────────────── */}
      <CollapsibleCard
        title="UTM Parameters"
        subtitle="Capture or hide utm_* query params"
      >
        <p style={S.hint}><strong>Show</strong> = capture from URL and save to the Lead. <strong>Hide</strong> = ignore entirely.</p>
        {UTM_KEYS.map((key) => (
          <div key={key} style={S.row}>
            <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "#0F172A", fontFamily: "monospace" }}>{key}</div>
            <div style={{ display: "flex", gap: 6 }}>
              <Pill active={settings.utmParameters[key].mode === "show"} onClick={() => update((s) => { s.utmParameters[key].mode = "show"; })}>Show</Pill>
              <Pill active={settings.utmParameters[key].mode === "hide"} onClick={() => update((s) => { s.utmParameters[key].mode = "hide"; })}>Hide</Pill>
            </div>
          </div>
        ))}
      </CollapsibleCard>

      {/* ── Lead defaults ────────────────────────────────────────────── */}
      <CollapsibleCard
        title="Lead Defaults"
        subtitle="Default field values written when a new Lead is created"
      >
        <p style={S.hint}>
          Applied <strong>create-only</strong> — repeat bookings that update an existing Lead (Lead Deduplication modes) do not overwrite these fields.
        </p>

        <div>
          <label style={S.label}>Default Stage Name</label>
          <input
            type="text"
            value={settings.leadDefaults?.stageName ?? ""}
            onChange={(e) => update((s) => {
              if (!s.leadDefaults) s.leadDefaults = {};
              s.leadDefaults.stageName = e.target.value;
            })}
            placeholder="New Lead (L1)"
            maxLength={100}
            style={S.input}
          />
          <p style={{ fontSize: 12, color: "#94A3B8", margin: "6px 0 0" }}>
            Written verbatim to <code>mek_stagename</code> on every newly created Lead. Leave empty to omit the field entirely (Dataverse default applies).
          </p>
        </div>
      </CollapsibleCard>

      {/* ── Lead deduplication ───────────────────────────────────────── */}
      <CollapsibleCard
        title="Lead Deduplication"
        subtitle="What happens when a Lead already exists for the attendee's email"
      >
        <p style={S.hint}>
          Match is by email only. Pick how repeat bookings should update the existing Lead's UTM attribution.
        </p>
        {[
          { value: "skip",         label: "Skip",                  caption: "First-touch — attributed Leads are never overwritten. Leads with no UTMs are backfilled from the booking (default)." },
          { value: "updateAll",    label: "Update all UTMs",       caption: "Last-touch — every booking overwrites all 6 UTM fields, even with direct visits." },
          { value: "updateIfReal", label: "Update only real UTMs", caption: "Hybrid — overwrites only when the URL has a real utm_source (not \"direct\")." },
          { value: "disabled",     label: "Disable dedup",         caption: "Always create a new Lead; duplicates are allowed in Dynamics." },
        ].map((opt) => {
          const active = settings.leadDeduplication?.mode === opt.value;
          return (
            <div key={opt.value} style={{ ...S.row, alignItems: "flex-start", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "flex-start", minWidth: 180 }}>
                <Pill
                  active={active}
                  onClick={() => update((s) => { s.leadDeduplication.mode = opt.value; })}
                >
                  {opt.label}
                </Pill>
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#64748B", lineHeight: 1.5, paddingTop: 6 }}>
                {opt.caption}
              </div>
            </div>
          );
        })}
      </CollapsibleCard>

      {/* ── Lead delivery ────────────────────────────────────────────── */}
      <CollapsibleCard
        title="Lead Delivery"
        subtitle="How the Dynamics Lead is written after a booking"
      >
        <p style={S.hint}>
          The booking response always returns as soon as the appointment is created — this only changes how the Lead is written afterward. Choose <strong>Queue</strong> only once the background worker Function App is provisioned (see DEPLOYMENT.md).
        </p>
        {[
          { value: "inline", label: "Inline",          caption: "Create the Lead in-process right after the response (default). Simple; no extra infrastructure." },
          { value: "queue",  label: "Queue (durable)", caption: "Enqueue the Lead for the background worker to create with retry + dead-letter. Survives worker recycling — requires the worker Function App." },
        ].map((opt) => {
          const active = settings.leadDelivery?.mode === opt.value;
          return (
            <div key={opt.value} style={{ ...S.row, alignItems: "flex-start", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "flex-start", minWidth: 180 }}>
                <Pill
                  active={active}
                  onClick={() => update((s) => { s.leadDelivery = { ...(s.leadDelivery || {}), mode: opt.value }; })}
                >
                  {opt.label}
                </Pill>
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#64748B", lineHeight: 1.5, paddingTop: 6 }}>
                {opt.caption}
              </div>
            </div>
          );
        })}
      </CollapsibleCard>

      {/* ── Availability ─────────────────────────────────────────────── */}
      <CollapsibleCard
        title="Availability"
        subtitle="Service hours vs. staff working hours, and which calendar events block a time"
      >
        <p style={S.hint}>
          Applies to services with their own hours in Bookings (Availability → <em>Custom hours</em>, e.g. a conference weekend). Real calendar events always block a time. Services set to "book when staff are free" always follow staff working hours.
        </p>
        {[
          { value: "everyDay",    label: "Every day",      caption: "Service hours override staff working hours on every day they apply — e.g. a Mon–Fri 8–5 staff member is bookable Sunday and until 7pm if the service says so (default)." },
          { value: "daysOffOnly", label: "Days off only",  caption: "Override only on days the staff member has no working hours (e.g. the weekend). On their working days a time must be inside both the service hours and their working hours." },
          { value: "never",       label: "Never",          caption: "Staff working hours always apply — a time must be inside both. Matches the Microsoft Bookings page." },
        ].map((opt) => {
          const active = (settings.availability?.serviceHoursOverride ?? "everyDay") === opt.value;
          return (
            <div key={opt.value} style={{ ...S.row, alignItems: "flex-start", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "flex-start", minWidth: 180 }}>
                <Pill
                  active={active}
                  onClick={() => update((s) => { s.availability = { ...(s.availability || {}), serviceHoursOverride: opt.value }; })}
                >
                  {opt.label}
                </Pill>
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#64748B", lineHeight: 1.5, paddingTop: 6 }}>
                {opt.caption}
              </div>
            </div>
          );
        })}

        <label style={{ ...S.label, marginTop: 20 }}>Calendar events that block a time</label>
        <p style={S.hint}>
          Each calendar event has a <em>Show as</em> status set by its owner (Busy, Tentative, Out of office, Free). All-day events marked Free never block under either option.
        </p>
        {[
          { value: "busyOnly",  label: CALENDAR_EVENT_LABELS.busyOnly,  caption: "Busy, Tentative and Out of office events block a time; events marked Free don't. Same as Outlook, Teams and the Microsoft Bookings page (default, recommended)." },
          { value: "allEvents", label: CALENDAR_EVENT_LABELS.allEvents, caption: "Every timed event on the staff member's calendar blocks a time, even ones marked Free — e.g. a conference whose agenda invites were sent as Free. Use per service where needed." },
        ].map((opt) => {
          const active = (settings.availability?.calendarEvents ?? "busyOnly") === opt.value;
          return (
            <div key={opt.value} style={{ ...S.row, alignItems: "flex-start", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "flex-start", minWidth: 180 }}>
                <Pill
                  active={active}
                  onClick={() => update((s) => { s.availability = { ...(s.availability || {}), calendarEvents: opt.value }; })}
                >
                  {opt.label}
                </Pill>
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#64748B", lineHeight: 1.5, paddingTop: 6 }}>
                {opt.caption}
              </div>
            </div>
          );
        })}
        <ServiceOverridePanel instance={instance} account={account} settings={settings} update={update}
          mapKey="calendarEventsByService" defaultKey="calendarEvents" defaultValue="busyOnly"
          labels={CALENDAR_EVENT_LABELS} ariaLabel="Calendar events that block a time" />

        <label style={{ ...S.label, marginTop: 24 }}>"Anyone available" option</label>
        <p style={S.hint}>
          Whether visitors can skip choosing a person. With it, the booking goes to the least busy team member who's free. Hidden, they must pick a specific person.
        </p>
        {[
          { value: "show", label: ANYONE_AVAILABLE_LABELS.show, caption: "Offer \"Anyone available\" next to the team members (default)." },
          { value: "hide", label: ANYONE_AVAILABLE_LABELS.hide, caption: "Visitors must choose a specific team member. Use per service, e.g. for named experts or account managers." },
        ].map((opt) => {
          const active = (settings.availability?.anyoneAvailable ?? "show") === opt.value;
          return (
            <div key={opt.value} style={{ ...S.row, alignItems: "flex-start", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "flex-start", minWidth: 180 }}>
                <Pill active={active} onClick={() => update((s) => { s.availability = { ...(s.availability || {}), anyoneAvailable: opt.value }; })}>
                  {opt.label}
                </Pill>
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#64748B", lineHeight: 1.5, paddingTop: 6 }}>{opt.caption}</div>
            </div>
          );
        })}
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 12, fontSize: 13, color: "#334155", lineHeight: 1.5, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={settings.availability?.honorBookingsStaffSelection !== false}
            onChange={(e) => update((s) => { s.availability = { ...(s.availability || {}), honorBookingsStaffSelection: e.target.checked }; })}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>Follow Bookings' "Let customers choose staff"</strong> — where a service has it turned off in Microsoft Bookings, visitors only get "Anyone available" (even if it's hidden above). Recommended.
          </span>
        </label>
        <ServiceOverridePanel instance={instance} account={account} settings={settings} update={update}
          mapKey="anyoneAvailableByService" defaultKey="anyoneAvailable" defaultValue="show"
          labels={ANYONE_AVAILABLE_LABELS} ariaLabel="Anyone available option"
          rowNote={(r) => (settings.availability?.honorBookingsStaffSelection !== false && r.staffSelectionAllowed === false
            ? "Bookings: customers can't choose staff — always \"Anyone available\""
            : null)} />
      </CollapsibleCard>

      {/* ── Duplicate questions ──────────────────────────────────────── */}
      <CollapsibleCard
        title="Duplicate Questions"
        subtitle="Service questions answered from the built-in Details fields"
      >
        <p style={S.hint}>
          Some Bookings services ask a custom question the booking form already collects — e.g. <em>Company Name</em> alongside the built-in <em>Company</em> field. Such a question is hidden and answered with the built-in field's value, so visitors answer once and Bookings still receives the answer.
        </p>
        {[
          { value: "auto",   label: "Auto-detect",  caption: "Match questions named like a built-in field (\"Company\", \"Company Name\", \"Phone Number\", \"Email\", \"Full Name\" …), plus any mappings below (default). Choice questions are never auto-detected." },
          { value: "manual", label: "Mappings only", caption: "Only the questions mapped below are autofilled." },
          { value: "off",    label: "Off",           caption: "Show every service question, even if it repeats a built-in field." },
        ].map((opt) => {
          const active = (settings.customQuestionAutofill?.mode ?? "auto") === opt.value;
          return (
            <div key={opt.value} style={{ ...S.row, alignItems: "flex-start", gap: 14 }}>
              <div style={{ display: "flex", justifyContent: "flex-start", minWidth: 180 }}>
                <Pill
                  active={active}
                  onClick={() => update((s) => { s.customQuestionAutofill = { map: [], ...(s.customQuestionAutofill || {}), mode: opt.value }; })}
                >
                  {opt.label}
                </Pill>
              </div>
              <div style={{ flex: 1, fontSize: 13, color: "#64748B", lineHeight: 1.5, paddingTop: 6 }}>
                {opt.caption}
              </div>
            </div>
          );
        })}

        <div style={{ marginTop: 16 }}>
          <label style={S.label}>Mappings</label>
          <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
            {(settings.customQuestionAutofill?.map || []).length === 0 && (
              <div style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>No mappings — auto-detect handles the usual names. Add one to map a differently-named question, or to stop a question being autofilled.</div>
            )}
            {(settings.customQuestionAutofill?.map || []).map((entry, i, arr) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, padding: "10px 12px", borderBottom: i < arr.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                <select
                  value={entry.questionId || ""}
                  onChange={(e) => update((s) => { s.customQuestionAutofill.map[i].questionId = e.target.value; })}
                  style={S.select}
                >
                  <option value="">— Select a question —</option>
                  {entry.questionId && !customQuestions.some((q) => q.id === entry.questionId) && (
                    <option value={entry.questionId}>{entry.questionId}</option>
                  )}
                  {customQuestions.map((q) => <option key={q.id} value={q.id}>{q.displayName}</option>)}
                </select>
                <select
                  value={entry.formField || "company"}
                  onChange={(e) => update((s) => { s.customQuestionAutofill.map[i].formField = e.target.value; })}
                  style={S.select}
                >
                  {[["company", "Company"], ["fullName", "Full name"], ["email", "Email address"], ["phone", "Phone number"], ["none", "Don't autofill (always ask)"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button type="button" style={S.danger} onClick={() => update((s) => { s.customQuestionAutofill.map.splice(i, 1); })}>Remove</button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => update((s) => {
              s.customQuestionAutofill = { mode: "auto", map: [], ...(s.customQuestionAutofill || {}) };
              s.customQuestionAutofill.map = [...(s.customQuestionAutofill.map || []), { questionId: "", formField: "company" }];
            })}
            style={{ ...S.secondary, marginTop: 10 }}
          >+ Add mapping</button>
        </div>
      </CollapsibleCard>

      {/* ── Manage booking ───────────────────────────────────────────── */}
      <CollapsibleCard
        title="Manage Booking"
        subtitle="Where customers cancel or reschedule from their confirmation email"
      >
        <p style={S.hint}>
          Microsoft Bookings' confirmation email has its own Reschedule / Cancel buttons. They open Microsoft's booking page — not this app — which also lists every service in the business. Applies to new bookings.
        </p>
        {[
          { key: "hideMicrosoftButtons", label: "Also mark new appointments as not self-managed", caption: "Sends isCustomerAllowedToManageBooking: false on each new appointment (only when our link is in the email). Bookings currently follows the service's setting instead — use the per-service list below to actually hide Microsoft's buttons." },
          { key: "emailManageLink",      label: "Add a \"Manage your booking\" link",        caption: "Adds a link to this app's Manage page in the email's Additional Information, where the customer can cancel or reschedule. Needs BOOKING_HOST_URL (https) on the server — without it no link is added. If you hide Microsoft's buttons, keep this on — otherwise customers have no way to manage their booking online." },
        ].map((opt) => (
          <div key={opt.key} style={{ ...S.row, alignItems: "flex-start", gap: 14 }}>
            <div style={{ paddingTop: 2 }}>
              <Toggle
                checked={settings.manageBooking?.[opt.key] ?? true}
                onChange={(v) => update((s) => { s.manageBooking = { hideMicrosoftButtons: true, emailManageLink: true, ...(s.manageBooking || {}), [opt.key]: v }; })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{opt.label}</div>
              <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.5, marginTop: 2 }}>{opt.caption}</div>
            </div>
          </div>
        ))}
        {(settings.manageBooking?.hideMicrosoftButtons ?? true) && settings.manageBooking?.emailManageLink === false && (
          <p style={{ fontSize: 12, color: "#B45309", margin: "10px 0 0" }}>
            With both off, customers can't cancel or reschedule online — only by contacting you.
          </p>
        )}
        <MicrosoftButtonsPanel instance={instance} account={account} toast={toast} />
      </CollapsibleCard>

      {/* ── Custom parameters ────────────────────────────────────────── */}
      <CollapsibleCard
        title="Custom URL Parameters"
        subtitle="Route extra query params to Lead / custom question / metadata"
      >
        <p style={S.hint}>Additional query parameters the link may carry (e.g. <code>?type=partner</code>). Each maps to a target field on the Lead, a Bookings custom question, or booking metadata.</p>
        {settings.customParameters.length === 0 && (
          <div style={{ padding: "14px", fontSize: 13, color: "#94A3B8", textAlign: "center", border: "1px dashed #E2E8F0", borderRadius: 10 }}>No custom parameters yet.</div>
        )}
        {settings.customParameters.map((cp, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 140px 1fr auto", gap: 10, padding: "10px 0", borderBottom: i < settings.customParameters.length - 1 ? "1px solid #F1F5F9" : "none", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", marginBottom: 4 }}>URL param</div>
              <input
                type="text"
                value={cp.urlParamName}
                placeholder="type"
                onChange={(e) => update((s) => { s.customParameters[i].urlParamName = e.target.value; })}
                style={S.input}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", marginBottom: 4 }}>Target</div>
              <select
                value={cp.mapTo?.target || "lead"}
                onChange={(e) => update((s) => { s.customParameters[i].mapTo = { ...s.customParameters[i].mapTo, target: e.target.value }; })}
                style={S.select}
              >
                <option value="lead">Lead field</option>
                <option value="customQuestion">Custom Question</option>
                <option value="metadata">Metadata only</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", marginBottom: 4 }}>
                {cp.mapTo?.target === "customQuestion" ? "Question ID" : cp.mapTo?.target === "metadata" ? "Metadata key" : "Lead logical name"}
              </div>
              <input
                type="text"
                value={cp.mapTo?.field || ""}
                placeholder={cp.mapTo?.target === "lead" ? "mek_linktype" : cp.mapTo?.target === "customQuestion" ? "Graph question id" : "e.g. source_type"}
                onChange={(e) => update((s) => { s.customParameters[i].mapTo = { ...s.customParameters[i].mapTo, field: e.target.value }; })}
                style={S.input}
              />
            </div>
            <button
              type="button"
              onClick={() => update((s) => { s.customParameters.splice(i, 1); })}
              style={{ ...S.danger, alignSelf: "end" }}
            >Remove</button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => update((s) => { s.customParameters.push({ urlParamName: "", mapTo: { target: "lead", field: "" } }); })}
          style={{ ...S.secondary, marginTop: 14 }}
        >+ Add custom parameter</button>
      </CollapsibleCard>

      {/* ── Calendar enrichment ─────────────────────────────────────── */}
      <CollapsibleCard
        title="Calendar"
        subtitle="How customer identity is shown on the staff Outlook event"
      >
        <p style={S.hint}>
          Microsoft Bookings masks customer details on multi-attendee calendar events.
          Pick how the booking platform should surface the customer name, company, and
          contact details on the assigned staff member's Outlook event.
        </p>
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Enrichment mode</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Pill
              active={(settings.calendar?.enrichmentMode || "internalNotes") === "internalNotes"}
              onClick={() => update((s) => { s.calendar = { ...(s.calendar || {}), enrichmentMode: "internalNotes" }; })}
            >Internal Notes</Pill>
            <Pill
              active={settings.calendar?.enrichmentMode === "patch"}
              onClick={() => update((s) => { s.calendar = { ...(s.calendar || {}), enrichmentMode: "patch" }; })}
            >Patch event</Pill>
            <Pill
              active={settings.calendar?.enrichmentMode === "both"}
              onClick={() => update((s) => { s.calendar = { ...(s.calendar || {}), enrichmentMode: "both" }; })}
            >Both</Pill>
          </div>
          <p style={{ ...S.hint, marginTop: 8 }}>
            <b>Internal Notes</b> sets the identity block in the appointment's <i>serviceNotes</i> — works with the existing <code>BookingsAppointment.ReadWrite.All</code> permission.
            {" "}<b>Patch event</b> rewrites the Outlook event's subject/body and adds the customer to the Tracking pane — requires the <code>Calendars.ReadWrite</code> application permission with tenant admin consent.
            {" "}<b>Both</b> runs Patch as the primary channel and keeps Internal Notes as a fallback.
          </p>
        </div>
      </CollapsibleCard>

      {/* ── Feature toggles ──────────────────────────────────────────── */}
      <CollapsibleCard
        title="Feature Toggles"
        subtitle="Global booking-flow features"
      >
        <p style={S.hint}>Turn individual booking-flow features on or off globally.</p>
        {[
          { key: "enableLeadCreation",   label: "Create Lead in Dataverse",    desc: "Create a Dynamics Lead when a booking is made." },
          { key: "enableProfilePhotos",  label: "Show staff profile photos",   desc: "Display host photos on the Staff and Time steps." },
          { key: "enableTimezonePicker", label: "Show timezone picker",        desc: "Let attendees change the timezone when choosing a time." },
        ].map((t) => (
          <div key={t.key} style={S.row}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{t.label}</div>
              <div style={{ fontSize: 12, color: "#64748B" }}>{t.desc}</div>
            </div>
            <Toggle
              checked={settings.featureToggles[t.key]}
              onChange={(v) => update((s) => { s.featureToggles[t.key] = v; })}
            />
          </div>
        ))}
      </CollapsibleCard>

      {/* ── Save bar (sticky at bottom of content) ──────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, padding: "16px 0" }}>
        {error && <span style={{ color: "#DC2626", fontSize: 13, marginRight: "auto" }}>{error}</span>}
        {!error && savedAt && <span style={{ color: "#16A34A", fontSize: 13, marginRight: "auto" }}>✓ Saved</span>}
        {!error && !savedAt && dirty && <span style={{ color: "#64748B", fontSize: 13, marginRight: "auto" }}>Unsaved changes</span>}
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={handleSave}
          style={{ ...S.primary, opacity: (!dirty || saving) ? 0.5 : 1, cursor: (!dirty || saving) ? "not-allowed" : "pointer" }}
        >
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </>
  );
}
